<template>
  <div class="output" :class="{ 'is-masked': burnStore.isPoolAbsence }">
    <div class="output__label">{{ $t('common.output') }}</div>
    <div class="output__container">
      <div
        v-if="
          isSoloPool
            ? burnStore.fromCurrency.token === burnStore.pool?.depositToken
            : true
        "
        class="output__col"
      >
        <div class="output__token">
          <TokenIcon
            :logo="fromToken?.logo"
            :type="fromToken?.type"
            size="24"
          />
          <div class="token-name-wrapper">
            <span class="token-name-wrapper__alias">
              {{ fromToken?.symbol }}
            </span>
            <span class="token-name-wrapper__provider">
              {{ fromProvider }}
            </span>
          </div>
          <token-alert :type="fromToken?.type" />
        </div>
        <div class="output__amount output__amount--first">
          <template v-if="burnStore.isUpdatingRate">
            <i class="pi pi-spin pi-spinner" style="font-size: 22px"></i>
          </template>
          <template v-else>
            {{
              isSoloPool
                ? soloOutput.formatted.value
                : outputFirst.formatted.value
            }}
          </template>
        </div>
      </div>
      <div
        v-if="
          isSoloPool
            ? burnStore.toCurrency.token === burnStore.pool?.depositToken
            : true
        "
        class="output__col"
      >
        <div class="output__token">
          <TokenIcon :logo="toToken?.logo" :type="toToken?.type" size="24" />
          <div class="token-name-wrapper">
            <span class="token-name-wrapper__alias">{{ toToken?.symbol }}</span>
            <span class="token-name-wrapper__provider"> {{ toProvider }}</span>
          </div>
          <token-alert :type="toToken?.type" />
        </div>
        <div class="output__amount output__amount--second">
          <template v-if="burnStore.isUpdatingRate">
            <i class="pi pi-spin pi-spinner" style="font-size: 22px"></i>
          </template>
          <template v-else>
            {{
              isSoloPool ? soloOutput.formatted : outputSecond.formatted.value
            }}
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBurnLiquidityStore, useStore, useTokensStore } from '@/store';
import { computed, ref } from 'vue';
import { useCurrencyFormat } from '@/composables/useCurrencyFormat';
import { TokenAlert } from '@/components/UI/TokenAlert';
import { TokenIcon } from '@/components/UI/TokenIcon';
import { providerForToken } from '@/utils/tokens';

const mainStore = useStore();
const burnStore = useBurnLiquidityStore();
const tokensStore = useTokensStore();
const isSoloPool = ref(!!burnStore.pool?.depositToken);

const fromToken = computed(() =>
  tokensStore.getToken(
    burnStore.fromCurrency.token || mainStore.defaultToken.value,
  ),
);
const toToken = computed(() =>
  tokensStore.getToken(
    burnStore.toCurrency.token || mainStore.defaultToken.value,
  ),
);

const soloOutput = useCurrencyFormat(
  computed(() => burnStore.soloExpectedOutput.value),
  computed(() => burnStore.pool?.depositToken),
  {
    useSuffix: false,
  },
);

const outputFirst = useCurrencyFormat(
  computed(() => burnStore.output?.withoutSlippage.x),
  computed(() => burnStore.fromCurrency.token),
  { useSuffix: false },
);

const outputSecond = useCurrencyFormat(
  computed(() => burnStore.output?.withoutSlippage.y),
  computed(() => burnStore.toCurrency.token),
  { useSuffix: false },
);

const toTokenEntity = computed(() => {
  if (burnStore.toCurrency.token) {
    return tokensStore.getToken(burnStore.toCurrency.token);
  }
  return undefined;
});

const fromTokenEntity = computed(() => {
  if (burnStore.toCurrency.token) {
    return tokensStore.getToken(burnStore.fromCurrency.token);
  }
  return undefined;
});

const toProvider = computed(() => {
  if (
    toTokenEntity.value?.type.length &&
    toTokenEntity.value?.type.length > 0
  ) {
    return providerForToken(toTokenEntity.value);
  }
  return '';
});

const fromProvider = computed(() => {
  if (
    fromTokenEntity.value?.type.length &&
    fromTokenEntity.value?.type.length > 0
  ) {
    return providerForToken(fromTokenEntity.value);
  }
  return '';
});
</script>

<style scoped lang="scss">
.output {
  transition: all 1s;

  &__label {
    margin-bottom: 12px;
    font-size: 20px;
    font-weight: 700;
    color: var(--text-color);
    display: flex;
    align-items: center;
    padding: 0 12px;
  }

  &__container {
    padding: 16px;
    border-radius: 16px;
    border: 1px solid transparentize(#e5e4fa, 0.85);
    display: flex;
    flex-direction: row;
    grid-gap: 16px;
    color: var(--text-color);
    position: relative;
  }

  &.is-masked {
    filter: blur(5px);
  }

  &__col {
    display: flex;
    flex-direction: column;
    grid-gap: 12px;
    width: 50%;
    overflow: hidden;
  }

  &__token {
    display: flex;
    flex-direction: row;
    grid-gap: 8px;
    font-size: 15px;
    font-weight: 500;
    align-items: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    img {
      width: 24px;
      height: 24px;
    }
  }

  :root &__amount {
    font-size: 30px;
    font-weight: 700;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &--first {
      letter-spacing: v-bind(
        'outputFirst.formatted.value.length > 8 ? "-1.5px" : "normal"'
      );
    }
    &--second {
      letter-spacing: v-bind(
        'outputSecond.formatted.value.length > 8 ? "-1.5px" : "normal"'
      );
    }
  }
}
</style>
